import { useEffect } from 'react'
import { focusableIn } from './focusable'
import { focusFirstAlive } from './focusFirstAlive'

/**
 * The regions F6 cycles between.
 *
 * **Semantic, not named.** Selecting `.toaster` by class would tie the rotor to
 * one component's skin and quietly stop working the day it is renamed — the
 * failure this repo records as file-named guards protecting filenames. A region
 * opts in by being one: a labelled landmark, the content area, or an open
 * dialog.
 *
 * `main[tabindex]` rather than `main`, matching `contentRegion` in
 * `focusFirstAlive`: focusing a `<main>` WITHOUT the attribute is a no-op that
 * reads as a successful move, so a layout that forgets it should fall through
 * rather than appear handled.
 *
 * Dialogs are in the list because of the case that made #216 worth fixing at
 * all. With an `Overlay` open the app is `inert` and the panel holds a focus
 * trap, so the queue and the dialog are the only two live regions on the page —
 * and a rotor that could reach the toast but never get back would be a trap of
 * its own.
 */
const REGIONS = [
  'main[tabindex]',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="region"][aria-label]',
  // **The navigation landmarks are deliberately NOT here yet.**
  //
  // They were, briefly: `nav[aria-label]` and `section[aria-label]` put the
  // sidebar's 25 links and the bottom tabs into the rotation, which makes this
  // module's name honest. web-architect then measured what it actually bought
  // and the answer was nothing testable — no unit coverage, nothing in `e2e/`
  // mentioning F6, and coverage decided by a spelling: `SettingsLayout` names
  // its nav with `aria-labelledby`, so the settings sub-nav was excluded while
  // the sidebar and breadcrumb were in, for no reason a reader could defend.
  //
  // A shell-wide keyboard behaviour with no coverage and an accidental
  // inclusion rule is its own card, not a rider on a toast issue. The
  // `aria-labelledby` question gets answered there.
].join(',')

/**
 * Move focus to a region, and say whether it actually went.
 *
 * **The rotor never makes a region focusable, and that is the correction.** The
 * first version set `tabindex="-1"` on any candidate that lacked one, which
 * turned "skip a region that declines focus" into a mechanism that could not
 * fire: the toast queue is an empty `<div>` whenever nothing is showing, and an
 * empty div handed a tabindex accepts focus perfectly well. So on any ordinary
 * page F6 moved focus into an invisible strip containing nothing, announced
 * "Notifications", and offered no onward Tab stop — which is most F6 presses in
 * the app. web-architect found it; the test that was supposed to cover it had
 * staged the refusal with a mocked `HTMLElement.prototype.focus` and was green
 * through the real behaviour, which is CLAUDE.md rule 8 exactly.
 *
 * A region is landable on its own terms or not at all:
 *
 *   - it DECLARES a tabindex — `<main tabindex="-1">`, and the toast queue only
 *     while it has something in it; or
 *   - it contains something tab-reachable, which is how a dialog panel is
 *     reached. Landing on the panel's first control is also the better
 *     behaviour: rotating back into a dialog should put the user on something
 *     operable, not on the box.
 *
 * `focusFirstAlive` does the confirming rather than a fourth hand-rolled copy
 * of it. It already knows the two lessons — `.focus()` on a detached or inert
 * node does nothing and throws nothing, and `<body>` is never a landing place.
 */
function focusRegion(region: HTMLElement): boolean {
  // Every candidate, not just the first. `focusFirstAlive` is varargs with
  // fall-through for precisely this reason, and handing it one element threw
  // that away: a region whose first tab-reachable child happened to decline —
  // disabled mid-flight, hidden — was skipped entirely rather than entered at
  // its second control. web-architect caught the wasted signature.
  return region.hasAttribute('tabindex')
    ? focusFirstAlive(region)
    : focusFirstAlive(...focusableIn(region))
}

/**
 * An F6 rotor across the page's regions — issue 216.
 *
 * **Why a rotor and not a longer clock.** A toast's action sat 27 tab stops
 * from the top of the document, and under an open `Overlay` there was no
 * keyboard route to it at all: the modal traps focus inside its panel by
 * design, so 15 and then 20 Tab presses never reached it. #194 answered the
 * first half by giving an actionable toast a 20s life instead of 5s, which
 * helps someone already tabbing and does nothing whatever for the second half.
 * This is the route itself; the clock goes back to being a convenience.
 *
 * F6 is the ARIA APG pattern for exactly this, and it is bound on `document`
 * rather than on any region — the whole point is to be reachable from wherever
 * focus currently is, including from inside a focus trap. `useFocusTrap` reads
 * `Tab` only and never sees this key, so the two do not fight.
 *
 * Mounted once, by `ToastProvider`, because the queue is the region that had no
 * route. That also puts it on the auth screens, which have toasts and no
 * chrome. The coupling is worth naming: if `ToastProvider` ever moves below a
 * route boundary the rotor goes with it, and no test would notice.
 *
 * **The second global key binding, and there is still no registry.**
 * `NotificationsDrawer` left that marker — *"when the palette lands there will
 * be two global bindings and no registry to hold them"* — and this is the
 * second. Not building one for two, but the next binding should not have to
 * rediscover the note.
 *
 * **Undiscoverable, and on macOS not even pressable by default.** F6 is the
 * ARIA APG pattern, so it is the right key, but the OS binds the function row
 * to hardware controls unless *Use F1, F2, etc. as standard function keys* is
 * on — so a Mac user needs Fn+F6. Nothing in the UI announces the route
 * either. Both are real limits on the claim that this reaches *every* keyboard
 * user, and neither is fixable from here: it wants the shortcut registry above
 * and somewhere to publish it.
 */
export function useRegionRotor(): void {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'F6') return
      const regions = [...document.querySelectorAll<HTMLElement>(REGIONS)]
      if (regions.length === 0) return

      // The region holding focus now, by containment rather than identity —
      // focus is almost always on a control INSIDE a region, not on the region.
      const active = document.activeElement
      const current = regions.findIndex((r) => r === active || r.contains(active))

      // `findIndex` returns -1 when focus is on <body>, which is the state
      // after any dismissal and on a fresh page. Treated as standing just
      // OUTSIDE the rotation, so F6 enters at the first region and Shift+F6 at
      // the last — the two ends, which is what a rotor entered from nowhere
      // should do. Both directions are pinned; an earlier comment here claimed
      // the first region either way, which the arithmetic never did.
      const step = event.shiftKey ? -1 : 1
      const from = current === -1 ? (step === 1 ? -1 : 0) : current

      // Every OTHER region, in rotation order, so a decline falls through to
      // the next rather than ending the move. The current one is excluded: F6
      // on the only live region should do nothing rather than re-announce it.
      for (let hop = 1; hop <= regions.length; hop += 1) {
        const index = (((from + hop * step) % regions.length) + regions.length) % regions.length
        const region = regions[index]
        if (region === undefined || index === current) continue
        if (focusRegion(region)) {
          // Consumed only once the move succeeded. F6 moves between browser UI
          // panes natively, and swallowing the key while doing nothing with it
          // is worse than letting the browser have it.
          event.preventDefault()
          return
        }
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])
}
